
import { useEffect, useLayoutEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "~/db/db";
import { initialPath } from "~misc/paths";
import { money } from "~hooks/useHelpers";
import {
  getLocalStorage,
  removeLocalStorage,
  saveLocalStorage,
} from "~services/utils/SaveIndexBD";

import useServico from "./useServico";
import useGrupoProduto from "./useGrupoProduto";
import useProduto from "./useProduto";
import usePrazoPagamento from "./usePrazoPagamento";
import useCliente from "./useCliente";
import { arrumaSimulacoes } from "./useSimulacoes";

const STORAGE_KEY = (clienteId) => `simulacao-${clienteId}`;

async function fetchSimulacaoDexie(idSimulacao) {
  if (!idSimulacao) return [];

  const result = [];
  await db.simulacoes
    .where("simulacaoId")
    .equals(Number(idSimulacao))
    .each((e) => result.push(e));

  return result;
}

function buildProdutoMap(produtos) {
  return produtos.reduce((acc, p) => {
    acc[p.id] = p;
    return acc;
  }, {});
}

function enrichProdutosComServico({
  gruposProduto,
  servicos,
  cliente,
}) {
  return (gruposProduto || []).map((grupo) => ({
    ...grupo,
    produtos: (grupo.produtos || []).map((produto) =>
      enrichProduto(produto, servicos, cliente)
    ),
  }));
}

function enrichProduto(produto, servicos, cliente) {
  const servicosMicro = (servicos || []).filter(
    (s) =>
      s.microrregiaoId === cliente?.microrregiaoId &&
      s.filialId === produto.filialId
  );

  const familiaId = produto.familiaId ?? produto.produtoOriginal?.familiaId;

  return {
    ...produto,
    fracionamento: findServicoPercentual(
      servicosMicro,
      "VC -CARGA FRACIONADA"
    ),
    descargaManual: findServicoPercentual(
      servicosMicro,
      "VC -DESCARGA CLIENTE",
      familiaId
    ),
  };
}

function findServicoPercentual(servicos, nome, familiaId) {
  const found = servicos.find(
    (s) =>
      s.denominacao === nome &&
      (familiaId ? s.familiaId === familiaId : true)
  );

  return found?.porcentagem ?? 0;
}

function buildSimulacaoFinal({
  simulacaoDexie,
  gruposProduto,
  prazoPagamentos,
  produtos,
}) {
  const simulacaoBase = arrumaSimulacoes(
    simulacaoDexie,
    gruposProduto,
    prazoPagamentos
  )[0];

  if (!simulacaoBase) return null;

  const produtoMap = buildProdutoMap(produtos);

  return {
    ...simulacaoBase,
    gruposProduto: (simulacaoBase.gruposMateriais || []).map((grupo) => ({
      ...grupo,
      grupoProduto: grupo.grupoMaterial,
      produtos: grupo.produtos.map((p) => ({
        ...p,
        produtoOriginal: produtoMap[p.produtoId] ?? {},
        grupoProdutoId: grupo.grupoProdutoId,
      })),
    })),
  };
}

const useSimulacao = (novaSimulacao = false, manualIdSimulacao = null) => {
  const navigate = useNavigate();
  const { simulacao: paramSimulacao, parameter: idCliente } = useParams();

  const idSimulacao = novaSimulacao
    ? paramSimulacao
    : manualIdSimulacao || paramSimulacao;

  const [simulacao, setSimulacao] = useState({});
  const [produtosDisponiveis, setProdutosDisponiveis] = useState([]);

  const { cliente } = useCliente(idCliente);
  const { servicos, setFiliais } = useServico({
    microrregiaoId: cliente?.microrregiaoId,
  });

  const { gruposProduto } = useGrupoProduto({});
  const { prazoPagamentos } = usePrazoPagamento({});
  const { produtos } = useProduto({ produtosDisponiveis, type: "geral" });

  useEffect(() => {
    loadSimulacaoFromDexie();
  }, [idSimulacao, produtos, gruposProduto, prazoPagamentos]);

  useLayoutEffect(() => {
    if (!idSimulacao && novaSimulacao) {
      removeLocalStorage(STORAGE_KEY(idCliente));
    }
    loadSimulacaoFromStorage();
  }, []);

  useEffect(() => {
    if (!servicos?.length) return;

    const enriched = enrichWithServices(simulacao);
    persistAndSet(enriched);
  }, [servicos]);

  async function loadSimulacaoFromDexie() {
    if (!idSimulacao) return;

    const dexieData = await fetchSimulacaoDexie(idSimulacao);
    if (!dexieData.length) return;

    setProdutosDisponiveis(dexieData.map((d) => d.produtoId));
    setFiliais(dexieData.map((d) => d.filialId));

    const simulacaoFinal = buildSimulacaoFinal({
      simulacaoDexie: dexieData,
      gruposProduto,
      prazoPagamentos,
      produtos,
    });

    saveLocalStorage(STORAGE_KEY(idCliente), simulacaoFinal, true);
    loadSimulacaoFromStorage();
  }

  function loadSimulacaoFromStorage() {
    const data = getLocalStorage(STORAGE_KEY(idCliente), true);
    if (!data) return;

    setSimulacao(enrichWithServices(data));
  }

  function enrichWithServices(data) {
    return {
      ...data,
      gruposProduto: enrichProdutosComServico({
        gruposProduto: data.gruposProduto,
        servicos,
        cliente,
      }),
    };
  }

  function persistAndSet(data) {
    saveLocalStorage(STORAGE_KEY(idCliente), data, true);
    setSimulacao(data);
  }

  function removeProduto(indexGP, indexProduto) {
    const copia = structuredClone(simulacao);

    copia.gruposProduto[indexGP].produtos.splice(indexProduto, 1);

    if (!copia.gruposProduto[indexGP].produtos.length) {
      copia.gruposProduto.splice(indexGP, 1);
    }

    persistAndSet(copia);
  }

  function quantidadeProduto(indexGP, index, valor) {
    const copia = structuredClone(simulacao);
    copia.gruposProduto[indexGP].produtos[index].quantidade = valor;
    persistAndSet(copia);
  }

  function setEdit(indexGP, index) {
    const produto = simulacao.gruposProduto[indexGP].produtos[index];
    saveLocalStorage(`simulacao-edit-produto${idCliente}`, produto, true);
    navigate(`${initialPath}/simuladores/simulacao/${idCliente}/editar`);
  }

  function changeFracionamentoDescargaManual(name) {
    persistAndSet({
      ...simulacao,
      [name]: !simulacao?.[name],
    });
  }

  function desconto(valorA, valorB) {
    const diff = Math.abs(valorA - valorB);
    const isDesconto = valorA > valorB;

    return (
      <p className={isDesconto ? "text-[#01AE3C]" : "text-[#FF2E00]"}>
        {(isDesconto ? "-" : "+") + money(diff)}
      </p>
    );
  }

  return {
    simulacao,
    setSimulacao,
    removeProduto,
    quantidadeProduto,
    setEdit,
    changeFracionamentoDescargaManual,
    desconto,
    getClienteId: () => idCliente,
  };
};

export default useSimulacao;
